import {useContext, useEffect, useRef, useState} from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {useTheme} from '@mui/material/styles';
import {
    Avatar,
    Badge,
    Box,
    Button,
    ButtonBase,
    CardActions,
    Chip,
    ClickAwayListener,
    Divider,
    Grid,
    Paper,
    Popper,
    Stack,
    TextField,
    Typography,
    useMediaQuery
} from '@mui/material';
import PerfectScrollbar from 'react-perfect-scrollbar';
import MainCard from 'ui-component/cards/MainCard';
import Transitions from 'ui-component/extended/Transitions';
import NotificationList from './NotificationList';
import {IconBell} from '@tabler/icons-react';
import { toast } from 'react-toastify';
import {AuthContext} from "../../../../context/AuthContext";
import Pusher from 'pusher-js';
import { apiFetchHeaders } from '../../../../helpers/api-fetch-headers';
import { TenantContext } from '../../../../context/TenantContext';
import Echo from 'laravel-echo';


// Notification status options
const notificationStatus = [
    {
        value: 'unreaded',
        label: 'Sin leer'
    },
    {
        value: 'readed',
        label: 'Leídas'
    },
    {
        value: 'all',
        label: 'Todas'
    },
];

// ==============================|| NOTIFICATION ||============================== //

const NotificationSection = () => {
    // Pusher.logToConsole = true; // log pusher events to debug on console

    const {currentUser, logout} = useContext(AuthContext);
    const {apiUrl} = useContext(TenantContext);
    
    const navigate = useNavigate();

    const theme = useTheme();
    const matchesXs = useMediaQuery(theme.breakpoints.down('md'));

    const [open, setOpen] = useState(false);
    const [selectedNotificationType, setSelectedNotificationType] = useState('unreaded');

    const [notifications, setNotifications] = useState([]);
    const [filteredNotifications, setFilteredNotifications] = useState([]);
    const [unreadedNotificationsCount, setUnreadedNotificationsCount] = useState(0);


    /* --------------- LIFECYCLES --------------- */

    const webSocketClient = new Echo({
        broadcaster: 'reverb',
        key: 'j6oqdtgnquhydb5jcu8k',
        wsHost: apiUrl,
        forceTLS: false,
        enabledTransports: ['ws', 'wss'],
    });

    useEffect(() => {
        webSocketClient.channel('notifications')
            .listen('.notification.created', (newNotification) => {
                    setNotifications(prevNotifications => {
                        const nuevoArray = [...prevNotifications, newNotification];
                        nuevoArray.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
                        return nuevoArray;
                    });
                    toast.info('Tienes una nueva notificación')
            });
    }, []);

    useEffect(() => {
        // getNotifications();

        // const pusher = new Pusher('4e5c151863e9f7855215', { cluster: 'eu' });
        // const channel = pusher.subscribe('notifications');
        //
        // const handleNewNotification = newNotification => {
        //     if (!newNotification.data){
        //         return false; // guard clause
        //     }
        //     if (newNotification.data && newNotification.data.user_id !== currentUser.id){
        //        return false; // As the notification is not for current user, dismiss it
        //     }
        //
        //     setNotifications(prevNotifications => {
        //         const nuevoArray = [...prevNotifications, newNotification.data];
        //         nuevoArray.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        //         return nuevoArray;
        //     });
        //
        //     // toast('Tienes una nueva notificación');
        //     toast('Tienes una nueva notificación',
        //         {
        //             icon: 'ℹ️',
        //             style: {
        //                 borderRadius: '10px',
        //                 background: '#333',
        //                 color: '#fff',
        //             },
        //         }
        //     );
        // };
        //
        // channel.bind('NewNotification', handleNewNotification);
        //
        // return () => {
        //     pusher.unsubscribe('notifications');
        //     pusher.disconnect();
        // };
    }, []);




    useEffect(() => {

        let filteredData = [...notifications];
        let unreaded = filteredData.filter((item) =>  item.readed === false);
        setUnreadedNotificationsCount(unreaded.length);


        switch (selectedNotificationType) {
            case 'readed':
                filteredData = filteredData.filter((item) => item.readed === true);
                break;
            case 'unreaded':
                filteredData = unreaded;
                break;
            default:
                //do nothing
                break;
        }

        setFilteredNotifications(filteredData);

    }, [notifications, selectedNotificationType]);

    /* ------------- END LIFECYCLES ------------- */


    /* --------------- FETCH DATA --------------- */
    const getNotifications = async () => {

        try {
            let response = await fetch(apiUrl + '/api/notifications/user/' + currentUser.id, {
                method: 'get',
                headers: apiFetchHeaders(),
            })

             // Log out user if is not authorized
            if (response.status === 401) {
                console.error('No authorized user and/or expired api token. Loggin out...');
                logout();
                navigate('/auth/login');
                return null;
            }
            if (!response.ok) {
                const message = `An error has occured at fetch call: ${response.status}`;
                throw new Error(message);
            }
            response = await response.json();

            if (response.error) throw new Error(response.message);

            setNotifications(response.data);


        } catch (error) {
            toast.error('Error al cargar tus notificaciones');
        } finally {
            // setWaitingApiData(false);
        }
    };
    const markAllNotificationsAsReaded = async () => {

        try {
            let response = await fetch(apiUrl + '/api/notifications/user/' + currentUser.id + '/markAllAsReaded', {
                method: 'get',
                headers: apiFetchHeaders(),
            })

             // Log out user if is not authorized
            if (response.status === 401) {
                console.error('No authorized user and/or expired api token. Loggin out...');
                logout();
                navigate('/auth/login');
                return null;
            }
            if (!response.ok) {
                const message = `An error has occured at fetch call: ${response.status}`;
                throw new Error(message);
            }
            response = await response.json();

            if (response.error) throw new Error(response.message);

            const updatedArray = [...notifications].map(item => ({ ...item, readed: true }));
            setNotifications(updatedArray);

        } catch (error) {
            toast.error('Error al marcar todas tus notificaciones como leídas');
        } finally {
            // setWaitingApiData(false);
        }
    };

    //////////////////////////////////////////////////



    /**
     * anchorRef is used on different componets and specifying one type leads to other components throwing an error
     * */
    const anchorRef = useRef(null);

    const handleToggle = () => {
        setOpen((prevOpen) => !prevOpen);
    };

    const handleClose = (event) => {
        if (anchorRef.current && anchorRef.current.contains(event.target)) {
            return;
        }
        setOpen(false);
    };

    const prevOpen = useRef(open);
    useEffect(() => {
        if (prevOpen.current === true && open === false) {
            anchorRef.current.focus();
        }
        prevOpen.current = open;
    }, [open]);

    const handleChange = (event) => {
        if (event?.target.value) setSelectedNotificationType(event?.target.value);
    };

    return (
        <>
            <Box
                sx={{
                    ml: 2,
                    mr: 3,
                    [theme.breakpoints.down('md')]: {
                        mr: 2
                    }
                }}
            >
                <Badge
                    // sx={{
                    //     "& .MuiBadge-badge": {
                    //         backgroundColor: theme.palette.primary.light
                    //     }
                    // }}
                    color={'primary'}
                    badgeContent={unreadedNotificationsCount}
                >
                    <ButtonBase sx={{borderRadius: '12px'}}>
                        <Avatar
                            variant="rounded"
                            sx={{
                                ...theme.typography.commonAvatar,
                                ...theme.typography.mediumAvatar,
                                transition: 'all .2s ease-in-out',
                                background: theme.palette.secondary.light,
                                color: theme.palette.secondary.dark,
                                '&[aria-controls="menu-list-grow"],&:hover': {
                                    background: theme.palette.secondary.dark,
                                    color: theme.palette.secondary.light
                                }
                            }}
                            ref={anchorRef}
                            aria-controls={open ? 'menu-list-grow' : undefined}
                            aria-haspopup="true"
                            onClick={handleToggle}
                            color="inherit"
                        >

                            <IconBell stroke={1.5} size="1.3rem"/>
                        </Avatar>
                    </ButtonBase>
                </Badge>
            </Box>
            <Popper
                placement={matchesXs ? 'bottom' : 'bottom-start'}
                open={open}
                anchorEl={anchorRef.current}
                role={undefined}
                transition
                disablePortal
                popperOptions={{
                    modifiers: [
                        {
                            name: 'offset',
                            options: {
                                offset: [matchesXs ? 5 : 0, 20]
                            }
                        }
                    ]
                }}
            >
                {({TransitionProps}) => (
                    <Transitions position={matchesXs ? 'top' : 'top-left'}  in={open} {...TransitionProps}>
                        <Paper>
                            <ClickAwayListener onClickAway={handleClose}>
                                <MainCard border={false} elevation={16} content={false} boxShadow
                                          shadow={theme.shadows[16]}>
                                    <Grid container direction="column" spacing={2}>
                                        <Grid item xs={12}>
                                            <Grid container spacing={8} alignItems="center"
                                                  justifyContent="space-between" sx={{pt: 2, px: 2}}>
                                                <Grid item>
                                                    <Stack direction="row" spacing={2}>
                                                        <Typography variant="subtitle1">Notificaciones</Typography>
                                                        <Chip
                                                            size="small"
                                                            label={filteredNotifications.length}
                                                            sx={{
                                                                color: theme.palette.background.default,
                                                                bgcolor: theme.palette.warning.dark
                                                            }}
                                                        />
                                                    </Stack>
                                                </Grid>
                                                <Grid item>

                                                    <Button>
                                                        <Typography component={Link} to="#" variant="subtitle2" color="primary" onClick={markAllNotificationsAsReaded} sx={{textTransform: 'initial'}}>
                                                            Marcar todas como leídas
                                                        </Typography>
                                                    </Button>

                                                </Grid>
                                            </Grid>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <PerfectScrollbar
                                                style={{
                                                    height: '100%',
                                                    maxHeight: 'calc(100vh - 205px)',
                                                    overflowX: 'hidden'
                                                }}
                                            >
                                                <Grid container direction="column" spacing={2}>
                                                    <Grid item xs={12}>
                                                        <Box sx={{px: 2, pt: 0.25}}>
                                                            <TextField
                                                                select
                                                                fullWidth
                                                                value={selectedNotificationType}
                                                                onChange={handleChange}
                                                                SelectProps={{
                                                                    native: true
                                                                }}
                                                            >
                                                                {notificationStatus.map((option) => (
                                                                    <option key={option.value} value={option.value}>
                                                                        {option.label}
                                                                    </option>
                                                                ))}
                                                            </TextField>
                                                        </Box>
                                                    </Grid>
                                                    <Grid item xs={12} p={0}>
                                                        <Divider sx={{my: 0}}/>
                                                    </Grid>
                                                </Grid>
                                                <NotificationList data={filteredNotifications}/>
                                            </PerfectScrollbar>
                                        </Grid>
                                    </Grid>
                                   {/* <Divider/>
                                    <CardActions sx={{p: 1.25, justifyContent: 'center'}}>
                                        <Button size="small" disableElevation>
                                            Ver todas
                                        </Button>
                                    </CardActions>*/}
                                </MainCard>
                            </ClickAwayListener>
                        </Paper>
                    </Transitions>
                )}
            </Popper>
        </>
    );
};

export default NotificationSection;
